/**
 * fetch wrapper for the dashboard API.
 *
 * All requests:
 *  - send cookies (`credentials: 'include'`)
 *  - JSON-encode bodies
 *  - attach the current org id as `X-Org-Id` so the backend scopes
 *    every read to one tenant (see api/auth/org_context.py)
 *  - attach the CSRF token (set by the OIDC session cookies) on unsafe methods
 *  - throw `ApiError` on non-2xx (with status + parsed detail)
 *  - on 401, transparently try to rotate the session via the shared
 *    @talis/auth-js refresh helper (→ POST /api/token/refresh) and retry once;
 *    if it still 401s, redirect to /sso/start to re-authenticate.
 */
import { tryRefreshToken, getCsrfToken } from "@talis/auth-js";

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown) {
    super(`API ${status}`);
    this.status = status;
    this.detail = detail;
  }
}

function ssoStart(): void {
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = `/sso/start?next=${next}`;
}

function buildHeaders(method: string, hasBody: boolean): Record<string, string> {
  const headers: Record<string, string> = {};
  if (hasBody) headers["Content-Type"] = "application/json";

  // The active org is no longer pinned by header — kraken is an org-aware
  // ait-auth app and the backend scopes by the JWT `org` claim. Org switching
  // goes through ait-auth (see api/orgs.ts).

  // CSRF token (re-read each call so a refresh that rotated the cookie is
  // reflected) on state-changing methods.
  const m = method.toUpperCase();
  if (m !== "GET" && m !== "HEAD" && m !== "OPTIONS") {
    const csrf = getCsrfToken();
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }
  return headers;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const hasBody = body !== undefined;
  const bodyText = hasBody ? JSON.stringify(body) : undefined;

  const doFetch = () =>
    fetch(path, {
      method,
      credentials: "include",
      headers: buildHeaders(method, hasBody),
      body: bodyText,
    });

  let res = await doFetch();

  if (res.status === 401) {
    // Access cookie may have just expired — try a silent refresh + one retry
    // before forcing a full re-auth. Refresh is best-effort: if it can't
    // recover the session, the retried request 401s again and we bounce to
    // /sso/start.
    const refreshed = await tryRefreshToken();
    if (refreshed) res = await doFetch();
    if (res.status === 401) {
      ssoStart();
      throw new ApiError(401, "unauthorized");
    }
  }

  if (res.status === 204) {
    return undefined as T;
  }

  let parsed: unknown;
  const text = await res.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, parsed);
  }

  return parsed as T;
}

export const api = {
  get:    <T = unknown>(path: string) => request<T>("GET", path),
  post:   <T = unknown>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch:  <T = unknown>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  put:    <T = unknown>(path: string, body?: unknown) => request<T>("PUT", path, body),
  delete: <T = unknown>(path: string) => request<T>("DELETE", path),
};
